import {
  enabledSizes,
  ingredientForSize,
  ingredientSizeMap,
  type PizzaBase,
  type PizzaConfig,
  type PizzaIngredientLike,
  type PizzaSize,
} from './pizza-config'
import { formatCents, parseMoneyToCents } from './money'
import { tt } from './i18n/translation-memory'

/**
 * Moved here from `manager-orders` on 2026-08-03 so the customer app can build a pizza too — §5 says
 * anything two modules need lives in `packages/*`, and FRONTEND.md §9.19 is the entry that needed it.
 * The logic is unchanged; only its address is.
 */

/**
 * Building a pizza on `/new-order` — the §14.6 configurator's staff-facing half.
 *
 * The config is already in `@agos/shared/pizza-config` with 22 tests and `/new-order` did not use it, so
 * a pizza could be added at its base price and not customised. Measured: two sizes (32 cm at 5,20 €,
 * 50 cm at 11,00 €), two bases (rajčinový and smotanový, 0,30 € and 1,00 € at both sizes), 38 ingredients
 * each carrying its own per-size price, and `garlicCrustEnabled: true` at `garlicCrustPrice: 0`.
 *
 * **Every price is per size and none of them scales.** `ingredientForSize` picks the exact size's values
 * and falls back to the nearest larger one; a 50 cm mozzarella is its own number, not a 32 cm one doubled.
 * So the total is the size's `basePrice`, plus the chosen base at that size, plus each ingredient at that
 * size, plus the garlic crust if it is taken — and nothing here multiplies anything by a ratio.
 *
 * **The crust is an ingredient, not a term here — §9.93.** An `Okraj` row is chosen and priced like any
 * other topping, which is why nothing below adds a crust to a total: it is already in `toppings`.
 * `PizzaConfig.garlicCrustEnabled` and `garlicCrustPrice` survive because the endpoint still carries
 * them, and are read by nothing.
 *
 * The line is written the way the customer app writes one — a name carrying the size and the choices —
 * because the kitchen board, the receipt and `/restaurant`'s bill all read that name and none of them
 * knows about this module.
 */

/**
 * An ingredient as this screen needs it.
 *
 * `PizzaIngredientLike` in `@agos/shared` is only the *pricing* shape — it carries `price32`, `sizeData`
 * and the rest, and deliberately no identity, because the costing helpers do not need one. A picker does.
 */
export interface BuilderIngredient extends PizzaIngredientLike {
  id: number
  name: string
  /**
   * The category row this ingredient sits in — read by {@link ingredientGroups}, and by §9.61 to
   * find the crust. Optional because a caller that only prices an ingredient does not need it.
   */
  category?: string | null
  /** Both spellings reach the builder: the entity says `active`, some payloads say `isActive`. */
  active?: boolean
  isActive?: boolean
}

export interface PizzaDraft {
  sizeCm: number | null
  baseValue: string | null
  /**
   * The toppings, in the order they were chosen — §9.43.
   *
   * **An id twice is a double portion.** One value rather than a set beside a `doubled` list: two
   * structures that have to agree are the shape §9.7 warns about, and keeping them agreeing would be
   * left to every handler. `pizzaPriceCents` sums per entry, so twice costs twice with no arithmetic
   * added, and `ingredientQty` reads it back for a screen. §9.678 - up to {@link MAX_PORTIONS} times.
   */
  ingredientIds: number[]
  /**
   * The name the customer or cashier typed - `FRONTEND.md` §9.680, owner #17, a missed port of legacy's
   * optional *Názov pizze*. Raw, as it sits in the input; {@link cleanPizzaName} is applied where the line is
   * named. Optional, so every draft built before this - and {@link EMPTY_PIZZA} - is unchanged.
   */
  name?: string
}

export const EMPTY_PIZZA: PizzaDraft = {
  sizeCm: null,
  baseValue: null,
  ingredientIds: [],
}

export function sizesOf(config: PizzaConfig | null): PizzaSize[] {
  return enabledSizes(config)
}

export function sizeOf(draft: PizzaDraft, config: PizzaConfig | null): PizzaSize | null {
  if (draft.sizeCm === null) return null
  return sizesOf(config).find((size) => size.cm === draft.sizeCm) ?? null
}

/**
 * A base's price at this size - `FRONTEND.md` §9.679, owner #18a, `OWNER-QUESTIONS.md` #119.
 *
 * **A base that is a *Pizza ingrediencie* row costs what that row says, per size**, through the same
 * `ingredientForSize` every topping is priced by - nearest size included, and exactly the figure the ingredient
 * screen prints. The owner's answer to #119: *charge the Pizza ingrediencie price everywhere - web and till
 * both take the base's per-size price from the ingredient table, not from the old `pizza_config.bases`.* This
 * looked the base up in `config.bases` by `value`, so a row-derived base (`ingredient-<id>`) priced at **0** -
 * Šereď's `+0,00 €` - and one whose label matched a config entry took the **config's** figure.
 *
 * **A base that exists only in `config.bases` keeps its config price** - a franchise with no `Základ` row has no
 * other figure for it - with the legacy single `price` as the fallback, as before.
 *
 * **`ingredients` is required, not optional**, for §9.480's and §9.518's reason: every caller is ours, and an
 * optional argument dropped at a call site compiles and silently restores the config price.
 */
export function basePriceCents(
  baseValue: string | null,
  config: PizzaConfig | null,
  sizeCm: number | null,
  ingredients: readonly BuilderIngredient[],
): number {
  if (baseValue === null || sizeCm === null) return 0
  const base = basesFor(config, ingredients).find((candidate) => candidate.value === baseValue)
  if (base === undefined) return 0
  if (base.ingredientId !== undefined) {
    const row = ingredients.find((candidate) => candidate.id === base.ingredientId)
    return row === undefined ? 0 : ingredientPriceCents(row, sizeCm)
  }
  const perSize = (base as { prices?: Record<string, unknown> }).prices
  const atSize = perSize?.[String(sizeCm)]
  if (atSize !== undefined) return parseMoneyToCents(atSize as string | number)
  return parseMoneyToCents((base as { price?: unknown }).price as string | number | null)
}

export function ingredientPriceCents(ingredient: PizzaIngredientLike, sizeCm: number): number {
  return parseMoneyToCents(ingredientForSize(ingredient, sizeCm).price)
}

export function chosenIngredients(
  draft: PizzaDraft,
  ingredients: readonly BuilderIngredient[],
): BuilderIngredient[] {
  // In the order they were chosen, not catalogue order — the description reads as it was built.
  return draft.ingredientIds.flatMap((id) => {
    const found = ingredients.find((candidate) => candidate.id === id)
    return found === undefined ? [] : [found]
  })
}

/** How many portions of one topping this draft carries: 0 up to {@link MAX_PORTIONS}. */
export function ingredientQty(draft: PizzaDraft, id: number): number {
  return draft.ingredientIds.filter((candidate) => candidate === id).length
}

/**
 * The most portions of one topping a pizza takes - `FRONTEND.md` §9.678, owner #18b.
 *
 * *"keď pridám dvojnásobne viac syru nech je tam 2x 3x"*: three, and a fourth tap removes it. Named because
 * three readers depend on it - the cycle in {@link toggleIngredient}, the first-tap argument in
 * {@link newPizza} and the tests - and a bare `3` in one of them is how they come to disagree.
 */
export const MAX_PORTIONS = 3

/**
 * What a topping's chip says about how much of it is on - §9.678.
 *
 * Nothing for an unchosen one, and `1×`, `2×`, `3×` otherwise. **The count is the whole signal now**: the owner
 * asked for *"2x 3x a nie nech mení farby"*, so every chosen count is drawn in the one selected colour and this
 * label is what tells them apart.
 */
export function portionLabel(qty: number): string {
  return qty > 0 ? `${qty}×` : ''
}

/**
 * What a topping's chip prices at - §9.678, *"the price multiplied"*.
 *
 * **One portion's price for an unchosen chip**, because that is what a tap adds, and the price of every portion
 * once it is on: a tripled cheese chip says three times the cheese, which is what it adds to the total. The chip
 * used to print one portion whatever the count, so it disagreed with the total beside it on every double.
 */
export function portionPriceCents(
  ingredient: PizzaIngredientLike,
  sizeCm: number,
  qty: number,
): number {
  return ingredientPriceCents(ingredient, sizeCm) * Math.max(1, qty)
}

/** Each chosen topping once, with how many portions — for a name or a tile, never for the price. */
export function distinctChosen(
  draft: PizzaDraft,
  ingredients: readonly BuilderIngredient[],
): { ingredient: BuilderIngredient; qty: number }[] {
  const seen = new Set<number>()
  return chosenIngredients(draft, ingredients).flatMap((ingredient) => {
    if (seen.has(ingredient.id)) return []
    seen.add(ingredient.id)
    return [{ ingredient, qty: ingredientQty(draft, ingredient.id) }]
  })
}

/**
 * One tap adds, the second and third each add a portion, a fourth removes it - §9.678, owner #18b, which
 * widened §9.43's *add, double, remove* to *"2x 3x"*.
 *
 * Each further entry goes in beside the first rather than at the end, so a doubled or tripled topping keeps
 * the position it was chosen at and the line reads the way it was built.
 */
export function toggleIngredient(draft: PizzaDraft, id: number): PizzaDraft {
  const qty = ingredientQty(draft, id)
  if (qty === 0) return { ...draft, ingredientIds: [...draft.ingredientIds, id] }
  if (qty >= MAX_PORTIONS) {
    return { ...draft, ingredientIds: draft.ingredientIds.filter((candidate) => candidate !== id) }
  }
  const more: number[] = []
  let placed = false
  for (const candidate of draft.ingredientIds) {
    more.push(candidate)
    // After the **first** entry only: a second pass would add one beside every portion already there.
    if (candidate === id && !placed) {
      more.push(id)
      placed = true
    }
  }
  return { ...draft, ingredientIds: more }
}

/** The whole pizza, in cents. A plain integer sum — every part is already cents (FB-056). */
export function pizzaPriceCents(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): number {
  const size = sizeOf(draft, config)
  if (size === null) return 0
  const base = parseMoneyToCents(size.basePrice)
  const sauce = basePriceCents(draft.baseValue, config, size.cm, ingredients)
  const toppings = chosenIngredients(draft, ingredients).reduce(
    (total, ingredient) => total + ingredientPriceCents(ingredient, size.cm),
    0,
  )
  // §9.93 — no crust term. An `Okraj` row is in `toppings` above, at its own per-size price; adding
  // it here as well billed a garlic crust twice, once as an ingredient and once as a crust.
  return base + sauce + toppings
}

/** What stops the pizza being added. */
export function pizzaProblems(draft: PizzaDraft, config: PizzaConfig | null): string[] {
  const found: string[] = []
  if (sizeOf(draft, config) === null) found.push(tt('Vyberte veľkosť'))
  if (draft.baseValue === null) found.push(tt('Vyberte základ'))
  return found
}

/**
 * The longest name a custom pizza takes - §9.680. Legacy set none; the server's `customName` is
 * `@Size(max = 400)` and the name shares it with the size and every topping, so it is held to forty.
 */
export const PIZZA_NAME_MAX = 40

/**
 * The typed name as it is stored - `FRONTEND.md` §9.680, owner #17.
 *
 * **Everything removed is something a reader of the stored line depends on**:
 *
 * - `( ) [ ]` - every reader finds the ingredient list at the first `(` and the extras at `[`.
 * - `+` - `StockDeduction` reads a name holding `Kombo` and `+` as a kombo and deducts whole pizzas by name.
 * - `·` - the base separator inside the list.
 * - `„ “ ” "` - they delimit the name, so {@link customPizzaNameOf} can read it back unambiguously.
 * - **the `cm` after a number** - `sizeCmFromName`, `PizzaSizes.sizeCmFrom` and the analytics query all take
 *   the **first** `NN cm` in the line, and the name comes before the size: a pizza named *Moja 50cm* would be a
 *   50 cm pizza to the stock, the margin report and the kitchen's size chip. The number stays.
 *
 * Whitespace is collapsed, the result is capped at {@link PIZZA_NAME_MAX} and trimmed again. Nothing left is
 * no name, which is the ordinary case.
 */
export function cleanPizzaName(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return ''
  const cleaned = raw
    .replace(/[()[\]+·„“”"]/g, ' ')
    .replace(/(\d)\s*cm/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned.slice(0, PIZZA_NAME_MAX).trim()
}

/**
 * The name a stored custom pizza was given, or `''` - §9.680, for reopening one at the till.
 *
 * Read only from **before** the ingredient list, so a topping can never be taken for a name, and cleaned again
 * on the way back so a line written by something else cannot hand the builder a name it would not store.
 */
export function customPizzaNameOf(lineName: string | null | undefined): string {
  if (typeof lineName !== 'string') return ''
  const open = lineName.indexOf('(')
  const head = open === -1 ? lineName : lineName.slice(0, open)
  return cleanPizzaName(/„([^“]*)“/.exec(head)?.[1] ?? '')
}

/**
 * The words every custom pizza's line starts with - `FRONTEND.md` §9.680, §9.763.
 *
 * **One spelling for the writer and the reader.** {@link pizzaLineName} writes it (through `tt`, as it always has)
 * and {@link isCustomPizzaLine} reads it back, so the kitchen recognises a custom pizza by what the builder wrote
 * rather than by a second copy of the words that could drift from the first.
 */
export const CUSTOM_PIZZA_NAME = 'Vlastná pizza'

/**
 * Whether a stored line is a custom pizza - `FRONTEND.md` §9.763, owner O125.
 *
 * Read off the start of the name {@link pizzaLineName} wrote: `Vlastná pizza – 32 cm (...)`, `Vlastná pizza „Moja“
 * – 32 cm (...)`, and the older `Vlastná pizza 32 cm` the customer checkout once stored. **The source words and
 * their translation both count**, because the stored name is whatever language the till that wrote it was in,
 * and the board reading it may be in another. Case is folded; nothing else is guessed.
 *
 * It says nothing about a catalogue product: a caller asks it only of a line with no catalogue row.
 */
export function isCustomPizzaLine(lineName: string | null | undefined): boolean {
  if (typeof lineName !== 'string') return false
  const head = lineName.trim().toLowerCase()
  return [CUSTOM_PIZZA_NAME, tt(CUSTOM_PIZZA_NAME)].some(
    (prefix) => prefix !== '' && head.startsWith(prefix.toLowerCase()),
  )
}

/**
 * The line's name, in the shape everything downstream already reads.
 *
 * `Vlastná pizza – 32 cm (Rajčinový základ, Šunka, Syr, cesnakový okraj)`. The kitchen board, the
 * receipt and the bill split all read this string; none of them knows this module exists, so the shape
 * is the contract.
 *
 * **§9.680 - a named pizza is `Vlastná pizza „Moja“ – 32 cm (...)`**, not legacy's bare `Moja – 32 cm (...)`:
 * `StockDeduction` sends a composed line without the word *pizza* down the lunch path, the margin report selects
 * `ILIKE '%vlastná pizza%'` and the gift rules count it by the word. The kitchen's title is the text before `(`.
 */
export function pizzaLineName(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): string {
  const size = sizeOf(draft, config)
  /* §9.647 - **the same source the picker offered**, or the kitchen ticket names a base the customer never saw.
   * This read `config.bases`, which for a franchise whose bases are ingredient rows answers nothing - so the
   * line simply lost its base - and with the first version's default substitution it answered the *wrong* one. */
  const base = basesFor(config, ingredients).find((candidate) => candidate.value === draft.baseValue)
  const parts = [
    ...(base === undefined ? [] : [base.label]),
    /* §9.678 - `2× Kukurica`, the count FIRST and for any count above one. This wrote `Kukurica 2×` and only for
     * exactly two, so a third portion would have printed a bare name. Leading is legacy's own spelling inside
     * the parentheses (`Home.tsx:923`), the one the kitchen's `MULTIPLIED_INGREDIENT` reads there, and the one
     * `StockDeduction` stripped - the trailing form matched no ingredient and never left the shelf (FB-998).
     * §9.41's `[Šunka 2×]` on a menu pizza's extras is a different shape in brackets and keeps its spelling. */
    ...distinctChosen(draft, ingredients).map(({ ingredient, qty }) =>
      qty > 1 ? `${qty}× ${ingredient.name}` : ingredient.name,
    ),
    // §9.73 — the crust's own name. With one crust this still reads *cesnakový okraj*; with two it
    // is the difference between a ticket the kitchen can act on and one that says a crust was wanted.
    // §9.93 — no crust here either: it is one of the toppings above, named in the order it was
    // chosen. Appending it again wrote it onto the line twice.
  ]
  const inside = parts.length === 0 ? '' : ` (${parts.join(', ')})`
  const named = cleanPizzaName(draft.name)
  const prefix = named === '' ? tt(CUSTOM_PIZZA_NAME) : `${tt(CUSTOM_PIZZA_NAME)} „${named}“`
  return `${prefix} – ${size === null ? '' : `${size.cm} cm`}${inside}`.replace('–  ', '– ')
}

export function pizzaSummary(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): string {
  if (sizeOf(draft, config) === null) return tt('Vyberte veľkosť a základ.')
  return `${pizzaLineName(draft, config, ingredients)} - ${formatCents(pizzaPriceCents(draft, config, ingredients))}`
}

/**
 * A custom pizza's cart id.
 *
 * Negative, like a lunch dish, because it is not a catalogue product — and distinct per configuration, so
 * two differently-built pizzas are two lines rather than one of quantity two.
 */
export function pizzaCartId(name: string): number {
  let hash = 5381
  for (let index = 0; index < name.length; index += 1) {
    hash = (Math.imul(hash, 33) ^ name.charCodeAt(index)) | 0
  }
  return hash >= 0 ? -(hash + 1) : hash
}

/**
 * The category legacy keeps out of the toppings, because it *is* the pizza's base.
 *
 * `pizza-builder-modal.tsx:77` reads it as the base selector — `i.category === "Základ"` — and its
 * `CATS` list never mentions it, so it cannot also appear as something to put on top. AGOS took its
 * bases from `/settings/pizza-config` instead, which answers the same two rows, and rendered the
 * whole ingredient table underneath: *Rajčinový základ* and *Smotanový základ* appeared twice on
 * the screen and could be charged for as toppings on a pizza that already had one.
 *
 * Raw Slovak, because it is a value stored in `pizza_ingredients.category` rather than anything a
 * reader sees — the heading is translated where it is drawn.
 */
export const PIZZA_BASE_CATEGORY = 'Základ'

/**
 * The bases a customer may choose, from where a franchise can actually set them - §9.647, owner report.
 *
 * <b>`config.bases` is not that place.</b> `pizza-config/SizesTab.tsx` reads it only to fill the *default base*
 * dropdown, and AGOS has **no base editor at all** - so a franchise's own bases are {@code pizza_ingredients}
 * rows in category {@link PIZZA_BASE_CATEGORY}, entered on the Ingredients screen. That is also where legacy's
 * customer builder reads them (`Home.tsx:853-856`) and the pattern §9.93 already applied to crusts, whose note
 * on that very tab says *"Okraje sú bežné ingrediencie"*.
 *
 * <p><b>Measured by the owner rather than by us</b>: Šereď's `config.bases` held three entries with no labels
 * while its real two are *Pomodoro pelato základ* and *Smotanovo bryndzový základ* - so
 * {@link PIZZA_BASE_CATEGORY}'s claim that the config <em>"answers the same two rows"</em> answers Šurany's two
 * and junk for a franchise that never had a localStorage blob to migrate.
 *
 * <p><b>The config is the fallback, and nothing is invented.</b> A franchise with no `Základ` ingredient keeps
 * whatever its config offers; a franchise with neither gets an empty list and a screen that says so, because a
 * substituted name reaches the kitchen ticket through {@code pizzaLineName}.
 *
 * <p><b>The price is the row's own, per size - §9.679, owner #18a, `OWNER-QUESTIONS.md` #119.</b> This entry
 * used to keep the **config's** price for a row whose label matched a config entry and nothing for one that did
 * not, deferring the question to the owner; #119 answered it: *charge the Pizza ingrediencie price everywhere*.
 * So a row-derived base carries {@link PizzaBaseChoice.ingredientId}, `basePriceCents` prices it from that row
 * through `ingredientForSize`, and its `prices` are filled from the same row so no reader of them sees the
 * config's figure. The **value** is still the matching config entry's, so a default base and a draft saved before
 * this read back unchanged.
 *
 * <p><b>§9.681 - this is the one list.</b> The picker, the price, the parts the server caps against, the default a
 * new pizza opens with and the reopen all read it, where four of them read `config.bases` and on Šereď's
 * nameless config found nothing.
 */
export function basesFor(
  config: PizzaConfig | null | undefined,
  ingredients: readonly BuilderIngredient[] | undefined,
): PizzaBaseChoice[] {
  const fromIngredients: PizzaBaseChoice[] = []
  for (const ingredient of ingredients ?? []) {
    const row = ingredient as BuilderIngredient & { category?: unknown; active?: unknown; isActive?: unknown }
    if (row.active === false || row.isActive === false) continue
    if (typeof row.category !== 'string' || row.category.trim() !== PIZZA_BASE_CATEGORY) continue
    if (typeof ingredient.name !== 'string' || ingredient.name.trim() === '') continue
    const label = ingredient.name.trim()
    const matching = (config?.bases ?? []).find((candidate) => candidate.label === label)
    fromIngredients.push({
      // The config entry's own value when there is one, so a draft saved before this reads back unchanged.
      value: matching?.value ?? `ingredient-${ingredient.id}`,
      label,
      prices: rowPrices(ingredient),
      ingredientId: ingredient.id,
    })
  }
  return fromIngredients.length > 0 ? fromIngredients : (config?.bases ?? [])
}

/**
 * A base as the builders offer it - a `PizzaBase`, and which *Pizza ingrediencie* row it is when it is one.
 *
 * `ingredientId` is absent for a base that exists only in `config.bases`, which is what tells `basePriceCents`
 * to keep that base's config price.
 */
export interface PizzaBaseChoice extends PizzaBase {
  ingredientId?: number
}

/** A row's per-size prices in euros, as `PizzaBase.prices` holds them - every size the row has a figure for. */
function rowPrices(ingredient: PizzaIngredientLike): Record<number, number> {
  const prices: Record<number, number> = {}
  for (const [cm, values] of ingredientSizeMap(ingredient)) prices[cm] = values.price
  return prices
}

/**
 * The category a crust lives in — `FRONTEND.md` §9.61, owner decision third round.
 *
 * Raw Slovak for the same reason {@link PIZZA_BASE_CATEGORY} is: it is a value stored in
 * `pizza_ingredients.category`, not a label anybody reads.
 */
export const PIZZA_CRUST_CATEGORY = 'Okraj'

/**
 * The category order: crust first, then legacy's cheese, meat, vegetables.
 *
 * Legacy's `CATS` is the last three and they are kept in its order — a habit worth not disturbing,
 * and re-sorting it alphabetically would reshuffle a screen nobody asked to have reshuffled.
 *
 * **`Základ` is first by §9.237, `Okraj` second by §9.179.** The base is chosen before the crust and
 * the crust before the cheese; legacy lists the base first of its four. Before §9.235 there was no
 * base group to rank — the customer builder still has none, because it passes `includeBase: false`.
 *
 * **§9.179's argument, applied once more.** §9.93 made the crust an ordinary topping rather than a special
 * control, and an ordinary topping outside this list ranks *after* all of it — so the first thing
 * anybody chooses about a pizza was sorted to the bottom. One list orders four screens: both
 * builders, the customer's toppings prompt and the manager's extras dialog.
 */
const KNOWN_CATEGORY_ORDER = [PIZZA_BASE_CATEGORY, 'Okraj', 'Syr', 'Mäso', 'Zelenina']

/** Where an ingredient with no category at all is put, rather than dropped. */
const UNCATEGORISED = 'Ostatné'

export interface IngredientGroup {
  /** The stored Slovak category, or `Ostatné`. Translate at the point it is drawn. */
  category: string
  ingredients: BuilderIngredient[]
}

/**
 * The toppings a person may choose, grouped under the category they were filed in.
 *
 * The owner's report is *"treba ich zoradiť podľa kategórií, ktoré si človek zvolí, toto je zle"*,
 * and both AGOS surfaces rendered one flat list in whatever order the endpoint answered.
 *
 * **The categories come from the data.** Legacy hard-codes three and filters to them, which means an
 * ingredient in a category a franchise adds later is not sorted last — it is not rendered at all, and
 * is unsellable with nothing on screen to say so. The three keep their order; anything else the
 * franchise has made follows alphabetically under its own heading, so adding a category adds a
 * section rather than losing an ingredient.
 *
 * **`active`, and `isActive` too.** The wire field is `active`. The customer app filtered on
 * `isActive`, which is on no row, so its guard has never removed anything — read both, and a surface
 * that switches an ingredient off gets what it asked for either way.
 */
export function ingredientGroups(
  ingredients: readonly BuilderIngredient[] | undefined,
  /**
   * Whether the **Základ** category is offered — §9.235.
   *
   * Off by default, which keeps the customer builder exactly as §9.93 left it: a customer chooses a
   * base separately and offering it again would charge for a second. The manager's product dialog
   * passes `true`, because there the chips describe what the product **is made of** and the base is
   * its first ingredient — *"I want to manually select which základ we will use"*.
   */
  includeBase = false,
): IngredientGroup[] {
  const groups = new Map<string, BuilderIngredient[]>()

  for (const ingredient of ingredients ?? []) {
    const row = ingredient as BuilderIngredient & {
      category?: unknown
      active?: unknown
      isActive?: unknown
      sortOrder?: unknown
    }
    if (row.active === false || row.isActive === false) continue
    /* §9.647, FB-975 - **a nameless ingredient is not offered either.** The owner's report was three unnameable
     * bases, and the same config carried ingredients the builder drew as empty chips: unpickable, and chargeable
     * if pressed. `active` was the only thing excluded here, which is one shape of unusable out of two. */
    if (typeof ingredient.name !== 'string' || ingredient.name.trim() === '') continue

    const category = typeof row.category === 'string' ? row.category.trim() : ''
    // §9.93 — only the base is excluded, because the pizza already has one and offering it as a
    // topping would charge for a second. An `Okraj` row is an ordinary ingredient now: the owner
    // asked for *"the same logic as other ingrediencies… you can select both of them"*, and that is
    // what an ingredient already does.
    if (!includeBase && category === PIZZA_BASE_CATEGORY) continue

    const key = category === '' ? UNCATEGORISED : category
    const bucket = groups.get(key)
    if (bucket) bucket.push(ingredient)
    else groups.set(key, [ingredient])
  }

  const rank = (category: string): number => {
    const known = KNOWN_CATEGORY_ORDER.indexOf(category)
    if (known !== -1) return known
    // Anything the franchise made goes after the three, and the uncategorised after that.
    return category === UNCATEGORISED ? Number.MAX_SAFE_INTEGER : KNOWN_CATEGORY_ORDER.length
  }

  return [...groups.entries()]
    .sort(([left], [right]) => {
      const byRank = rank(left) - rank(right)
      return byRank !== 0 ? byRank : left.localeCompare(right, 'sk')
    })
    .map(([category, rows]) => ({
      category,
      // `sortOrder` is what the ingredient editor writes when somebody drags a row, and it is spaced
      // in tens — so a tie is two rows nobody has ordered, and the name decides rather than the
      // order the database happened to answer in.
      ingredients: [...rows].sort((left, right) => {
        const leftOrder = Number((left as { sortOrder?: unknown }).sortOrder ?? 0)
        const rightOrder = Number((right as { sortOrder?: unknown }).sortOrder ?? 0)
        const byOrder =
          (Number.isFinite(leftOrder) ? leftOrder : 0) - (Number.isFinite(rightOrder) ? rightOrder : 0)
        return byOrder !== 0 ? byOrder : left.name.localeCompare(right.name, 'sk')
      }),
    }))
}

/** One part of a composed line, as `POST /orders` takes it — `BACKEND.md §10A.14`. */
export interface PizzaPart {
  productId?: number
  name: string
  unitPriceCents: number
  quantity: number
}

/**
 * What a custom pizza is assembled from, for the server to cap the line against — §10A.14.
 *
 * **The sum of these is `pizzaPriceCents` exactly**, and deliberately so: the parts are the same four
 * terms that function adds, read the same way. A cap that did not agree with the price would either
 * refuse honest baskets or leave the hole `BLOCKED.md` #12 is about.
 *
 * **Almost none of them has a catalogue row.** A size, a base and a topping are not products, so they
 * are sent at the price the builder charged and the server takes that figure. That is not a hole: it
 * is the same figure the line itself would have carried, and the cap still stops a line claiming more
 * than the parts it declares. Only a part naming a real product can be re-priced, and a custom pizza
 * has none.
 *
 * A double portion is **one part with `quantity: 2`** — §9.43's multiset read back — because two
 * entries for the same topping would be a different claim about what the kitchen makes.
 */
export function pizzaParts(
  draft: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[],
): PizzaPart[] {
  const size = sizeOf(draft, config)
  if (size === null) return []

  const parts: PizzaPart[] = [
    { name: size.label, unitPriceCents: parseMoneyToCents(size.basePrice), quantity: 1 },
  ]

  /* §9.681 - `basesFor`, the list the picker drew. This read `config.bases`, so a base that is a *Pizza
   * ingrediencie* row (every one of Šereď's) was left out of the parts and a reopen had no base to read back -
   * and once §9.679 charges that row's price, the parts would no longer sum to the line the server caps. */
  const base = basesFor(config, ingredients).find((candidate) => candidate.value === draft.baseValue)
  if (base !== undefined) {
    parts.push({
      name: base.label,
      unitPriceCents: basePriceCents(draft.baseValue, config, size.cm, ingredients),
      quantity: 1,
    })
  }

  for (const { ingredient, qty } of distinctChosen(draft, ingredients)) {
    parts.push({
      name: ingredient.name,
      unitPriceCents: ingredientPriceCents(ingredient, size.cm),
      quantity: qty,
    })
  }

  // §9.93 — no crust part. §9.61's rule stands and is why this had to go with the other two: the
  // parts must sum to the price, and the price no longer carries a crust term because an `Okraj` row
  // is one of the toppings emitted above.
  return parts
}

/** Legacy's cheese, matched on a substring of the name. Its own rule, and there is no id to read. */
const DEFAULT_CHEESE = 'mozzarella'

/**
 * A fresh pizza for this franchise — `FRONTEND.md` §9.60, §9.188.
 *
 * `EMPTY_PIZZA` is the blank one and stays blank; this is the one a builder should open with.
 *
 * **A franchise's `defaultBaseValue` wins, and the first base is the fallback — §9.188.** This
 * paragraph used to argue the opposite: *"`bases[0]` in particular is the wrong fallback: the array's
 * order is the order somebody typed them in, so it would move the preselection whenever a base is
 * added or reordered."* That is true and it is the smaller problem. `defaultBaseValue` is **empty on
 * the owner's data**, so the honest consequence of refusing a fallback was a builder that preselected
 * nothing for every customer — while legacy takes `pizzaBases[0].id` and always has.
 *
 * **An unrecognised default still preselects nothing of its own** and falls to the first base rather
 * than to a substitute the franchise did choose but which no longer exists.
 *
 * **The cheese is legacy's `name.includes('mozzarella')`**, one portion. Fragile, and the alternative
 * here is nothing: there is no `defaultIngredientIds` in the config to read. Only if it is offered —
 * a franchise without mozzarella gets no cheese, not the first ingredient in the list.
 *
 * **One portion and not two**, legacy's own: a preselected double is a second portion of cheese the customer
 * never asked for, and with §9.678's cycle (0 → 1 → 2 → 3 → off) their first tap would make it three.
 */
export function newPizza(
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[] = [],
): PizzaDraft {
  // §9.681 - the list the picker offers. `config.bases` alone is empty on a franchise whose bases are rows
  // (Šereď), so a fresh pizza opened with no base and *Vyberte základ* until somebody chose one.
  const bases = basesFor(config, ingredients)
  const wanted = (config?.defaultBaseValue ?? '').trim()
  const chosen = bases.find((base) => base.value === wanted)?.value ?? bases[0]?.value ?? null

  const cheese = ingredients.find(
    (ingredient) =>
      ingredient.name.toLowerCase().includes(DEFAULT_CHEESE) &&
      (ingredient as { active?: boolean; isActive?: boolean }).active !== false &&
      (ingredient as { active?: boolean; isActive?: boolean }).isActive !== false,
  )

  return {
    ...EMPTY_PIZZA,
    baseValue: chosen,
    ingredientIds: cheese === undefined ? [] : [cheese.id],
  }
}

/**
 * A draft's defaults once the data has answered - FRONTEND.md §9.717, FB-1019.
 *
 * §9.60 applies {@link newPizza} when the panel opens, and a panel opened before the config and the ingredients
 * answered got no base and no cheese for good. This answers `newPizza` for a draft still **untouched** - no base, no
 * ingredient, no name - keeping any size already picked, and the draft itself otherwise; and the same object when
 * there is still nothing to apply, so a caller in an effect settles in one pass.
 */
export function pizzaDraftOnLoad(
  current: PizzaDraft,
  config: PizzaConfig | null,
  ingredients: readonly BuilderIngredient[] = [],
): PizzaDraft {
  const untouched = current.baseValue === null && current.ingredientIds.length === 0 && (current.name ?? '') === ''
  if (!untouched) return current
  const fresh = newPizza(config, ingredients)
  if (fresh.baseValue === null && fresh.ingredientIds.length === 0) return current
  return { ...fresh, sizeCm: current.sizeCm ?? fresh.sizeCm }
}
